type LogContext = Record<string, unknown>;

function emit(tag: string, message: string, context?: LogContext) {
  const line = context
    ? `${tag} ${message} ${JSON.stringify(context)}`
    : `${tag} ${message}`;
  console.log(line);
}

function emitWarn(tag: string, message: string, context?: LogContext) {
  const line = context
    ? `${tag} ${message} ${JSON.stringify(context)}`
    : `${tag} ${message}`;
  console.warn(line);
}

function emitError(tag: string, message: string, err?: unknown, context?: LogContext) {
  const line = `${tag} ${message} ${JSON.stringify({
    ...context,
    error:
      err instanceof Error
        ? { name: err.name, message: err.message }
        : err !== undefined
          ? String(err)
          : undefined,
  })}`;
  console.error(line);
}

/** Structured WebRTC pipeline audit logger — filter console by tag prefix. */
export const webrtcAudit = {
  media(message: string, context?: LogContext) {
    emit('[Media]', message, context);
  },
  permissions(message: string, context?: LogContext) {
    emit('[Permissions]', message, context);
  },
  socket(message: string, context?: LogContext) {
    emit('[Socket]', message, context);
  },
  offer(message: string, context?: LogContext) {
    emit('[Offer]', message, context);
  },
  answer(message: string, context?: LogContext) {
    emit('[Answer]', message, context);
  },
  ice(message: string, context?: LogContext) {
    emit('[ICE]', message, context);
  },
  peer(message: string, context?: LogContext) {
    emit('[Peer]', message, context);
  },
  track(message: string, context?: LogContext) {
    emit('[Track]', message, context);
  },
  video(message: string, context?: LogContext) {
    emit('[Video]', message, context);
  },
  screenShare(message: string, context?: LogContext) {
    emit('[ScreenShare]', message, context);
  },
  warn(tag: keyof typeof TAGS, message: string, context?: LogContext) {
    emitWarn(TAGS[tag], message, context);
  },
  error(tag: keyof typeof TAGS, message: string, err?: unknown, context?: LogContext) {
    emitError(TAGS[tag], message, err, context);
  },
  auditSecureContext() {
    if (typeof window === 'undefined') return;
    const secure = window.isSecureContext;
    const protocol = window.location.protocol;
    const host = window.location.host;
    webrtcAudit.media('Secure context check', {
      isSecureContext: secure,
      protocol,
      host,
      hasMediaDevices: typeof navigator.mediaDevices !== 'undefined',
      hasGetUserMedia: typeof navigator.mediaDevices?.getUserMedia === 'function',
    });
    if (!secure && !host.startsWith('localhost') && !host.startsWith('127.0.0.1')) {
      webrtcAudit.permissions('INSECURE — camera/mic blocked on this origin', {
        protocol,
        host,
        hint: 'Use https:// on port 5173 for phone camera/mic',
      });
    }
  },
  auditIceServers(servers: Array<{ urls: string | string[] }>) {
    const urls = servers.flatMap((s) => (Array.isArray(s.urls) ? s.urls : [s.urls]));
    const hasStun = urls.some((u) => u.startsWith('stun:'));
    const hasTurn = urls.some((u) => u.startsWith('turn:'));
    webrtcAudit.ice('ICE server configuration', { urls, hasStun, hasTurn });
    if (!hasTurn) {
      webrtcAudit.warn(
        'ice',
        'No TURN servers — cross-NAT / strict networks may fail ICE',
        { configured: urls },
      );
    }
  },
};

const TAGS = {
  media: '[Media]',
  permissions: '[Permissions]',
  socket: '[Socket]',
  offer: '[Offer]',
  answer: '[Answer]',
  ice: '[ICE]',
  peer: '[Peer]',
  track: '[Track]',
  video: '[Video]',
  screenShare: '[ScreenShare]',
} as const;

if (typeof window !== 'undefined') {
  webrtcAudit.auditSecureContext();
}
